/**
 * Admin-user management (Owner-only).
 *
 * One surface tying together an operator's three login methods — password,
 * login barcode, and OIDC. List + approve pending SSO rows, change roles,
 * disable/enable, force-revoke sessions, and register/generate/revoke login
 * barcodes (generated ones render as a printable QR). Functional; visual polish
 * is a later refinement.
 */

import { useCallback, useEffect, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Input,
  Modal,
  Popconfirm,
  Popover,
  QRCode,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { CopyOutlined, QrcodeOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";

import {
  type AdminBarcodeRow,
  type AdminUserRow,
  ApiError,
  api,
} from "../../api/client";

const ROLES = ["owner", "office", "logistics", "viewer", "pos"];

function errText(e: unknown): string {
  return e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e);
}

function statusColor(status: string): string {
  if (status === "active") return "green";
  if (status === "disabled") return "red";
  return "gold"; // pending_*
}

function credentialWindow(b: AdminBarcodeRow): string | null {
  const f = b.valid_from ? new Date(b.valid_from).toLocaleString() : null;
  const t = b.valid_until ? new Date(b.valid_until).toLocaleString() : null;
  if (!f && !t) return null;
  return `${f ?? "…"} → ${t ?? "…"}`;
}

export default function AdminUsersPage() {
  const { message } = AntdApp.useApp();
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [methodsUser, setMethodsUser] = useState<AdminUserRow | null>(null);
  const [pendingRole, setPendingRole] = useState<Record<number, string>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createdBarcode, setCreatedBarcode] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listAdminUsers());
    } catch (e) {
      message.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      await load();
    } catch (e) {
      message.error(errText(e));
    }
  };

  const columns = [
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Email", dataIndex: "email", key: "email", render: (e: string | null) => e ?? "—" },
    {
      title: "Login methods",
      key: "methods",
      render: (_: unknown, u: AdminUserRow) => (
        <Space size={4} wrap>
          {u.has_password && <Tag>password</Tag>}
          {u.barcode_count > 0 && <Tag color="blue">barcode ×{u.barcode_count}</Tag>}
          {u.oidc_subject && <Tag color="purple">SSO</Tag>}
        </Space>
      ),
    },
    {
      title: "Role",
      key: "role",
      render: (_: unknown, u: AdminUserRow) => {
        const isPending = u.status.startsWith("pending");
        if (isPending) {
          return (
            <Space>
              <Select
                size="small"
                style={{ width: 120 }}
                placeholder="role"
                value={pendingRole[u.id]}
                options={ROLES.map((r) => ({ value: r, label: r }))}
                onChange={(v) => setPendingRole((p) => ({ ...p, [u.id]: v }))}
              />
              <Button
                size="small"
                type="primary"
                disabled={!pendingRole[u.id]}
                onClick={() =>
                  run(
                    () => api.approveAdminUser(u.id, pendingRole[u.id]),
                    "Approved",
                  )
                }
              >
                Approve
              </Button>
            </Space>
          );
        }
        return (
          <Select
            size="small"
            style={{ width: 120 }}
            value={u.role ?? undefined}
            disabled={u.status === "disabled"}
            options={ROLES.map((r) => ({ value: r, label: r }))}
            onChange={(v) => run(() => api.changeAdminUserRole(u.id, v), "Role changed")}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, u: AdminUserRow) => (
        <Space size={4} wrap>
          <Button size="small" onClick={() => setMethodsUser(u)}>
            Login methods
          </Button>
          {u.status !== "disabled" && !u.status.startsWith("pending") && (
            <Popconfirm
              title="Disable this user?"
              onConfirm={() => run(() => api.disableAdminUser(u.id), "Disabled")}
            >
              <Button size="small" danger>
                Disable
              </Button>
            </Popconfirm>
          )}
          {u.status === "disabled" && (
            <Button
              size="small"
              onClick={() => run(() => api.enableAdminUser(u.id), "Enabled")}
            >
              Enable
            </Button>
          )}
          <Popconfirm
            title="Revoke all sessions for this user?"
            onConfirm={() =>
              run(async () => {
                const r = await api.revokeAdminUserSessions(u.id);
                return r;
              }, "Sessions revoked")
            }
          >
            <Button size="small">Revoke sessions</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Admin users"
      extra={
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          Create user
        </Button>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        size="small"
        pagination={false}
      />
      <LoginMethodsModal
        key={methodsUser?.id ?? "closed"}
        user={methodsUser}
        onClose={() => {
          setMethodsUser(null);
          void load();
        }}
      />
      <CreateUserModal
        key={createOpen ? "open" : "closed"}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(bc) => {
          setCreateOpen(false);
          void load();
          if (bc) setCreatedBarcode(bc);
        }}
      />
      <Modal
        open={!!createdBarcode}
        title="Login barcode"
        onCancel={() => setCreatedBarcode(null)}
        footer={<Button onClick={() => setCreatedBarcode(null)}>Done</Button>}
      >
        {createdBarcode && (
          <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <QRCode value={createdBarcode} size={180} />
            <code>{createdBarcode}</code>
            <Typography.Text type="secondary">
              Hand/print this — scanning it logs the operator in.
            </Typography.Text>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (barcode: string | null) => void;
}) {
  const { message } = AntdApp.useApp();
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("viewer");
  const [genBarcode, setGenBarcode] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!username.trim()) {
      message.error("Username is required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.createAdminUser({
        username: username.trim(),
        role,
        generate_barcode: genBarcode,
      });
      message.success(`Created ${res.user.username}`);
      setUsername("");
      setRole("viewer");
      setGenBarcode(true);
      onCreated(res.barcode);
    } catch (e) {
      message.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Create admin user"
      okText="Create"
      confirmLoading={busy}
      onOk={submit}
      onCancel={onClose}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Input
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onPressEnter={submit}
        />
        <Select
          style={{ width: "100%" }}
          value={role}
          options={ROLES.map((r) => ({ value: r, label: r }))}
          onChange={setRole}
        />
        <Space>
          <Switch checked={genBarcode} onChange={setGenBarcode} />
          <span>Generate a login barcode (no email/password needed)</span>
        </Space>
      </Space>
    </Modal>
  );
}

const LABEL_W = 120;

function LoginMethodsModal({
  user,
  onClose,
}: {
  user: AdminUserRow | null;
  onClose: () => void;
}) {
  const { message } = AntdApp.useApp();
  // Local copy so password/SSO removals reflect live in the modal; the parent
  // table reloads on close.
  const [u, setU] = useState<AdminUserRow | null>(user);
  const [barcodes, setBarcodes] = useState<AdminBarcodeRow[]>([]);
  const [value, setValue] = useState("");
  const [kind, setKind] = useState<"login" | "api">("login");
  const [comment, setComment] = useState("");
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [qrShow, setQrShow] = useState<string | null>(null);
  const [pwInput, setPwInput] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    setU(user);
  }, [user]);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      setBarcodes(await api.listAdminUserBarcodes(user.id));
    } catch (e) {
      message.error(errText(e));
    }
  }, [user, message]);

  useEffect(() => {
    if (user) void refresh();
  }, [user, refresh]);

  if (!u) return null;

  const create = async (opts: { barcode?: string; generate?: boolean }) => {
    try {
      await api.addAdminUserBarcode(u.id, {
        ...opts,
        kind,
        comment: comment.trim() || undefined,
        valid_from: validFrom ? validFrom.toISOString() : undefined,
        valid_until: validUntil ? validUntil.toISOString() : undefined,
      });
      setValue("");
      setComment("");
      setValidFrom(null);
      setValidUntil(null);
      await refresh();
    } catch (e) {
      message.error(errText(e));
    }
  };

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text);
    message.success("Copied");
  };

  const toggle = async (b: AdminBarcodeRow) => {
    try {
      if (b.disabled_at) await api.enableAdminUserBarcode(u.id, b.id);
      else await api.disableAdminUserBarcode(u.id, b.id);
      await refresh();
    } catch (e) {
      message.error(errText(e));
    }
  };

  const setPassword = async () => {
    const pw = pwInput;
    if (!pw) {
      message.error("Enter a password");
      return;
    }
    try {
      setU(await api.setAdminUserPassword(u.id, pw));
      setPwInput("");
      setPwOpen(false);
      message.success("Password set");
    } catch (e) {
      message.error(errText(e));
    }
  };

  const clearPassword = async () => {
    try {
      setU(await api.clearAdminUserPassword(u.id));
      message.success("Password removed");
    } catch (e) {
      message.error(errText(e));
    }
  };

  const unlinkOidc = async () => {
    try {
      setU(await api.unlinkAdminUserOidc(u.id));
      message.success("SSO unlinked");
    } catch (e) {
      message.error(errText(e));
    }
  };

  return (
    <Modal
      open={!!user}
      title={`Login methods — ${u.username}`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {/* Password */}
        <Space align="center" wrap>
          <Typography.Text strong style={{ width: LABEL_W, display: "inline-block" }}>
            Password
          </Typography.Text>
          {u.has_password ? <Tag color="green">Set</Tag> : <Tag>Not set</Tag>}
          <Popover
            open={pwOpen}
            onOpenChange={(o) => {
              setPwOpen(o);
              if (!o) setPwInput("");
            }}
            trigger="click"
            title={u.has_password ? "Reset password" : "Set a password"}
            content={
              <Space direction="vertical" style={{ width: 240 }}>
                <Input.Password
                  autoFocus
                  placeholder="New password"
                  value={pwInput}
                  onChange={(e) => setPwInput(e.target.value)}
                  onPressEnter={() => void setPassword()}
                />
                <Button
                  type="primary"
                  size="small"
                  block
                  disabled={!pwInput}
                  onClick={() => void setPassword()}
                >
                  {u.has_password ? "Reset password" : "Set password"}
                </Button>
              </Space>
            }
          >
            <Button size="small">
              {u.has_password ? "Reset" : "Set password"}
            </Button>
          </Popover>
          {u.has_password && (
            <Popconfirm
              title="Remove this user's password?"
              description="They keep their other login methods."
              onConfirm={clearPassword}
            >
              <Button size="small" danger>
                Remove
              </Button>
            </Popconfirm>
          )}
        </Space>

        {/* Single sign-on */}
        <Space align="center" wrap>
          <Typography.Text strong style={{ width: LABEL_W, display: "inline-block" }}>
            Single sign-on
          </Typography.Text>
          {u.oidc_subject ? <Tag color="purple">Linked</Tag> : <Tag>Not linked</Tag>}
          {u.oidc_subject && <code style={{ fontSize: 12 }}>{u.oidc_subject}</code>}
          {u.oidc_subject && (
            <Popconfirm
              title="Unlink SSO?"
              description="The user can sign in with SSO again to re-link."
              onConfirm={unlinkOidc}
            >
              <Button size="small" danger>
                Unlink
              </Button>
            </Popconfirm>
          )}
        </Space>

        {/* Login barcodes & API tokens */}
        <Typography.Text strong>Login barcodes &amp; API tokens</Typography.Text>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space wrap>
            <Select
              size="small"
              value={kind}
              style={{ width: 140 }}
              onChange={(v) => setKind(v as "login" | "api")}
              options={[
                { value: "login", label: "Login barcode" },
                { value: "api", label: "API token" },
              ]}
            />
            <Input
              size="small"
              placeholder="comment (optional)"
              style={{ width: 180 }}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Space>
          <Space wrap>
            <DatePicker
              size="small"
              showTime
              placeholder="valid from"
              value={validFrom}
              onChange={(d) => setValidFrom(d)}
            />
            <DatePicker
              size="small"
              showTime
              placeholder="valid until (expiry)"
              value={validUntil}
              onChange={(d) => setValidUntil(d)}
            />
          </Space>
          {kind === "login" ? (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="Scan or type a barcode"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onPressEnter={() =>
                  value.trim() && void create({ barcode: value.trim() })
                }
              />
              <Button
                type="primary"
                disabled={!value.trim()}
                onClick={() => void create({ barcode: value.trim() })}
              >
                Add
              </Button>
              <Button onClick={() => void create({ generate: true })}>
                Generate
              </Button>
            </Space.Compact>
          ) : (
            <Button type="primary" onClick={() => void create({})}>
              Mint API token
            </Button>
          )}
        </Space>

        {barcodes.length === 0 && <Tag>No credentials registered.</Tag>}
        {barcodes.map((b) => (
          <Space key={b.id} align="center" wrap>
            <Tag color={b.kind === "api" ? "geekblue" : "blue"}>
              {b.kind === "api" ? "API" : "login"}
            </Tag>
            {b.comment && <span>{b.comment}</span>}
            <code style={{ fontSize: 12 }}>
              {b.barcode.length > 18 ? `${b.barcode.slice(0, 18)}…` : b.barcode}
            </code>
            {b.disabled_at && <Tag color="red">disabled</Tag>}
            {credentialWindow(b) && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {credentialWindow(b)}
              </Typography.Text>
            )}
            {b.last_used_at && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                used {new Date(b.last_used_at).toLocaleString()}
              </Typography.Text>
            )}
            {b.kind === "login" && (
              <Button
                size="small"
                icon={<QrcodeOutlined />}
                title="Show login QR"
                aria-label="Show login QR"
                onClick={() => setQrShow(b.barcode)}
              />
            )}
            <Button
              size="small"
              icon={<CopyOutlined />}
              title="Copy value"
              aria-label="Copy credential value"
              onClick={() => copy(b.barcode)}
            />
            <Button size="small" onClick={() => void toggle(b)}>
              {b.disabled_at ? "Enable" : "Disable"}
            </Button>
            <Popconfirm
              title="Remove this credential?"
              onConfirm={async () => {
                try {
                  await api.deleteAdminUserBarcode(u.id, b.id);
                  await refresh();
                } catch (e) {
                  message.error(errText(e));
                }
              }}
            >
              <Button size="small" danger>
                Remove
              </Button>
            </Popconfirm>
          </Space>
        ))}
      </Space>
      <Modal
        open={!!qrShow}
        title="Scan to sign in"
        onCancel={() => setQrShow(null)}
        footer={<Button onClick={() => setQrShow(null)}>Close</Button>}
      >
        {qrShow && (
          <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <QRCode value={qrShow} size={200} />
            <code>{qrShow}</code>
            <Button icon={<CopyOutlined />} onClick={() => copy(qrShow)}>
              Copy
            </Button>
          </Space>
        )}
      </Modal>
    </Modal>
  );
}
